import { findMarqueeAnimation } from './findMarqueeAnimation';

type SnapArgs = Readonly<{
	/** The overflow-clipped wrapper - its box is the visible window. */
	container: HTMLElement;
	/** The animated <ul>; its width is twice the half-track the keyframe slides across. */
	track: HTMLElement;
	/** The element that received focus. */
	target: EventTarget | null;
	/** Whether the track runs with animation-direction: reverse. */
	reverse: boolean;
}>;

/**
 * Reports whether `element` currently has keyboard (`:focus-visible`) focus, as opposed to focus
 * from a click. Snapping on click focus would yank the card out from under the cursor mid-click
 * and swallow the navigation, so only keyboard focus snaps. Engines without the selector fail
 * open: snapping too often beats leaving focus off-screen.
 */
function isKeyboardFocus(element: Element): boolean {
	try {
		return element.matches(':focus-visible');
	} catch {
		return true;
	}
}

function getKeyboardFocusedCard(target: EventTarget | null): HTMLLIElement | undefined {
	if (!(target instanceof Element) || !isKeyboardFocus(target)) return undefined;
	return target.closest('li') ?? undefined;
}

/**
 * Returns how far the track must move for the card to fit; when the window is narrower than a
 * card, aligning the left edge wins.
 */
function getShift(containerRect: DOMRect, cardRect: DOMRect): number {
	let shift = 0;
	if (cardRect.right > containerRect.right) shift = containerRect.right - cardRect.right;
	if (cardRect.left + shift < containerRect.left) shift = containerRect.left - cardRect.left;
	return shift;
}

/**
 * Seeks the marquee's CSS animation so a keyboard-focused card sits fully inside the window.
 * The track is already paused (group-focus-within) when this runs, so the seek reads as a
 * single reposition, and the track is periodic so any phase is valid to resume from.
 */
export function snapFocusedCardIntoView({ container, track, target, reverse }: SnapArgs): void {
	const card = getKeyboardFocusedCard(target);
	const animation = card && findMarqueeAnimation(track);
	if (!card || !animation) return;
	const duration = animation.effect?.getTiming().duration;
	const half = track.offsetWidth / 2;
	if (typeof duration !== 'number' || duration <= 0 || half <= 0) return;

	const containerRect = container.getBoundingClientRect();
	const shift = getShift(containerRect, card.getBoundingClientRect());
	if (shift === 0) return;

	const currentTranslate = track.getBoundingClientRect().left - containerRect.left;
	const seekTime = getSeekTime(currentTranslate + shift, half, duration, reverse);
	if (seekTime !== null) animation.currentTime = seekTime;
}

/**
 * Maps the target translate onto the animation clock; null when it falls outside the
 * keyframe's (-half, 0] range - only first-half cards always resolve inside it, and a card
 * that can't be placed in the window shouldn't be faked with its lookalike.
 */
function getSeekTime(translate: number, half: number, duration: number, reverse: boolean): number | null {
	if (translate > 0 || translate < -half) return null;
	const progress = reverse ? 1 + translate / half : -translate / half;
	// an exact 1 wraps to the next iteration (a half-track jump) - stop just short of it
	return Math.min(Math.max(progress, 0), 1 - 1e-6) * duration;
}
